import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import type { SpeechSettings } from './interpreter.types';
import { isInterpreterLanguage, labels, languageCodes } from './interpreterPage.constants';
export type { SpeechSettings } from './interpreter.types';
interface Props {
  settings: SpeechSettings;
  onChange: (settings: SpeechSettings) => void;
  devices: MediaDeviceInfo[];
  deviceId: string;
  onDeviceChange: (id: string) => void;
  onRefreshDevices: () => void;
  disabled?: boolean;
  languagesLocked?: boolean;
}
export const InterpreterSettings = ({
  settings,
  onChange,
  devices,
  deviceId,
  onDeviceChange,
  onRefreshDevices,
  disabled,
  languagesLocked,
}: Props) => (
  <div className="space-y-5">
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="interpreter-first-language">Fyrra tungumál</Label>
        <Select
          value={settings.clinicianLanguage}
          disabled={disabled || languagesLocked}
          onValueChange={(value) => {
            if (value !== 'is' && value !== 'en') return;
            onChange({
              ...settings,
              clinicianLanguage: value,
              patientLanguage: settings.patientLanguage === value ? 'no' : settings.patientLanguage,
            });
          }}
        >
          <SelectTrigger id="interpreter-first-language">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="is">Íslenska</SelectItem>
            <SelectItem value="en">Enska</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="interpreter-second-language">Seinna tungumál</Label>
        <Select
          value={settings.patientLanguage}
          disabled={disabled || languagesLocked}
          onValueChange={(value) => {
            if (isInterpreterLanguage(value)) onChange({ ...settings, patientLanguage: value });
          }}
        >
          <SelectTrigger id="interpreter-second-language">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languageCodes
              .filter((language) => language !== settings.clinicianLanguage)
              .map((language) => (
                <SelectItem key={language} value={language}>
                  {labels[language]}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="space-y-2">
      <Label htmlFor="interpreter-mode">Lotuskipting</Label>
      <Select value="manual" disabled={disabled}>
        <SelectTrigger id="interpreter-mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="manual">Handvirk</SelectItem>
          <SelectItem value="automatic" disabled>
            Sjálfvirk (síðar)
          </SelectItem>
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        Veldu tungumál beggja ræðumanna. Ýttu á „Enda tal“ til að ljúka hverri lotu.
      </p>
    </div>
    <div className="space-y-2">
      <Label htmlFor="interpreter-microphone">Hljóðnemi</Label>
      <div className="flex gap-2">
        <Select
          value={deviceId || '__default'}
          disabled={disabled}
          onValueChange={(value) => onDeviceChange(value === '__default' ? '' : value)}
        >
          <SelectTrigger id="interpreter-microphone">
            <SelectValue placeholder="Sjálfgefinn hljóðnemi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default">Sjálfgefinn hljóðnemi</SelectItem>
            {deviceId && !devices.some((device) => device.deviceId === deviceId) && (
              <SelectItem value={deviceId}>Vistaður hljóðnemi</SelectItem>
            )}
            {devices
              .filter((device) => device.deviceId)
              .map((device, index) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Hljóðnemi ${index + 1}`}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Button variant="outline" disabled={disabled} onClick={onRefreshDevices}>
          Sækja hljóðnema
        </Button>
      </div>
    </div>
    {(['speakClinician', 'speakPatient'] as const).map((key) => (
      <div key={key} className="flex items-center justify-between gap-3">
        <Label htmlFor={`interpreter-${key}`}>
          Upplestur (
          {labels[key === 'speakClinician' ? settings.clinicianLanguage : settings.patientLanguage].toLowerCase()})
        </Label>
        <Switch
          id={`interpreter-${key}`}
          checked={settings[key]}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ ...settings, [key]: checked })}
        />
      </div>
    ))}
  </div>
);
